// Pure indexing helpers for warranty visits on the calendar. Unlike scheduled
// tasks (single-day), warranty visits are date-RANGED, so each visit is FANNED
// OUT across every day in [visitDateStart..visitDateEnd] (inclusive) — it
// shows as a per-day pill on each day it covers, mirroring how a multi-day job
// assignment appears on each of its days. Kept pure + separate for unit testing.

import type { CalendarWarrantyVisitRow } from '@gutterinv/shared';
import { iterateDaysInclusive } from './calendar-dates.js';
import { NO_PEOPLE_ROW_ID } from '../components/CalendarWeekView.js';

/** Index visits by `${personId}:${day}` across the full range. A visit with N
 *  people over D days lands in N×D buckets; a visit with zero people lands in
 *  the synthetic NO_PEOPLE_ROW bucket so it never disappears. */
export function indexWarrantyByPersonDay(
  visits: CalendarWarrantyVisitRow[],
): Map<string, CalendarWarrantyVisitRow[]> {
  const idx = new Map<string, CalendarWarrantyVisitRow[]>();
  for (const v of visits) {
    const personIds = v.people.length > 0 ? v.people.map((p) => p.id) : [NO_PEOPLE_ROW_ID];
    for (const day of iterateDaysInclusive(v.visitDateStart, v.visitDateEnd)) {
      for (const pid of personIds) {
        const key = `${pid}:${day}`;
        const list = idx.get(key);
        if (list) list.push(v);
        else idx.set(key, [v]);
      }
    }
  }
  return idx;
}

/** Index visits by day (month view), fanned out across the full range. */
export function indexWarrantyByDate(
  visits: CalendarWarrantyVisitRow[],
): Map<string, CalendarWarrantyVisitRow[]> {
  const idx = new Map<string, CalendarWarrantyVisitRow[]>();
  for (const v of visits) {
    for (const day of iterateDaysInclusive(v.visitDateStart, v.visitDateEnd)) {
      const list = idx.get(day);
      if (list) list.push(v);
      else idx.set(day, [v]);
    }
  }
  return idx;
}

/** True if any visit has no people — feeds the "(no people yet)" swimlane. */
export function anyWarrantyUnassigned(visits: CalendarWarrantyVisitRow[]): boolean {
  return visits.some((v) => v.people.length === 0);
}
